import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect, useSelector } from "react-redux";
import styled from "styled-components";
import { Heading } from "../../components/Heading";
import { CompanyCard } from "../../newui/features/Company/components/CompanyCard";
import {
	getCompanies,
	getCompaniesByPage,
	DEFAULT_PAGE_SIZE,
} from "../../actions/companyActions";
import { Button } from "components/Button";
import "styles/loading.scss";
import { TabContent } from "../../components/Tabs/styled";
import { Icon } from "assets/Icon";
import CreateSupplierDrawer from "./drawer/CreateSupplierDrawer";
import AddSupplierDrawer from "./drawer/AddSupplierDrawer";
import { Flex, SkeletonCircle } from "@chakra-ui/react";
import { SearchBox } from "components/SearchBox";
import { Toolbar } from "components/Toolbar";
import { Input } from "components/RFQ/Input";
import { removeFromList } from "../../actions/searchActions";
import SkeletonBase from "newui/components/Skeleton";
import { Grid, GridItem } from "@chakra-ui/react";
import { Pagination } from "components/Pagination";
import { useDispatch } from "react-redux";
import debounce from "lodash.debounce";
import { useCurrentCompanyId, useCurrentCompanyValue } from "hooks/auth";
import { exportPdf } from "newui/features/Settings/PdfSettings/api/design-schema-api";
import { downloadFileNoPopup } from "helpers/linkHelper";
import axios from "axios";
import { useCurrentUserHasFeatureFlag, useHasCustomiseShortlistReport } from "hooks/featureFlags";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";
import { ShortlistDownloadModal, CustomiseReportModal } from "newui/features/ShortlistReport";

// Define sort options outside the component as it's static
const sortList = [
	{
		value: "name",
		label: "Sort Alphabetically (A-Z)",
	},
	{
		value: "-name",
		label: "Sort Alphabetically (Z-A)",
	},
];

const ShortlistStyled = styled.div`
	padding: ${(props) =>
		props.padded ? `${props.theme.space.lg} ${props.theme.space.md}` : 0};

	${(props) =>
		props.isInSearchShortlist &&
		`
        margin: 0;
    `}

	.shortlisted-text {
		font-size: ${(props) => props.theme.fontSizes.xxl};
		line-height: 32px;
		margin-bottom: 0px;
	}
`;

const ShortlistContainer = React.memo(
	({
		getCompanies,
		items,
		noHeading,
		padded,
		isInSearchShortlist = false,
		handleRemoveButtonClick,
		linkNewTab,
		user,
		removeFromList,
		isDataLoading,
		initialSortBy,
		paginationMeta,
		isAdminMode,
		...props
	}) => {
		const [showCreateDrawer, setShowCreateDrawer] = useState(false);
		const [showAddDrawer, setShowAddDrawer] = useState(false);
		const [isLoading, setIsLoading] = useState(false);
		const [idSupplierList, setIdSupplierList] = useState("");
		const [sortBy, setSortBy] = useState(initialSortBy || "name");
		const [keyword, setKeyword] = useState("");
		const search = useSelector((state) => state.search);
		const currentCompanyId = useCurrentCompanyId();
		const isRfxEnabled = useCurrentCompanyValue("enable_rfx_functionality");
		const newShortlistReport = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_NEW_SHORTLIST_REPORT);
		const canCustomise = useHasCustomiseShortlistReport();
		const [showDownloadModal, setShowDownloadModal] = useState(false);
		const [showCustomiseModal, setShowCustomiseModal] = useState(false);
		const dispatch = useDispatch();

		// Memoize filtered and sorted companies calculation
		const companies = useMemo(() => {
			if (isInSearchShortlist) {
				return search.shortlist
					.filter((companySearch) => {
						if (!keyword) return true;
						const searchTerm = keyword.toLowerCase();
						return (
							companySearch.name?.toLowerCase().includes(searchTerm) ||
							companySearch.company_number
								?.toLowerCase()
								.includes(searchTerm) ||
							companySearch.description?.toLowerCase().includes(searchTerm)
						);
					})
					.sort((a, b) => {
						if (sortBy === "name") {
							return a.name.localeCompare(b.name);
						} else if (sortBy === "-name") {
							return b.name.localeCompare(a.name);
						}
						return 0;
					});
			}
			return items?.length ? items : search?.searchResult || [];
		}, [
			isInSearchShortlist,
			search.shortlist,
			search.searchResult,
			items,
			keyword,
			sortBy,
		]);

		// Memoize expensive list computations
		const { listIDCompanies, listCompanies } = useMemo(() => {
			const listIDCompanies = [];
			const listCompanies = [];

			for (let a = 0; a < companies?.length; a++) {
				listIDCompanies.push(companies[a].id);
				listCompanies.push({
					label: companies[a]?.name,
					value: companies[a]?.id,
				});
			}

			return { listIDCompanies, listCompanies };
		}, [companies]);

		const totalPages = search?.paginationMeta?.total;

		useEffect(() => {
			let url = new URL(window.location.href);
			let params = new URLSearchParams(url.search);
			let supplier = params.get("supplier");
			if (supplier) {
				setIdSupplierList(supplier);
			}
		}, []);

		// debounce the API call to prevent excessive requests
		const debouncedFetchCompanies = useMemo(
			() =>
				debounce((searchKeyword, currentSortBy) => {
					setIsLoading(true);
					try {
						getCompanies(searchKeyword, currentSortBy);
					} finally {
						setIsLoading(false);
					}
				}, 300),
			[getCompanies]
		);

		const fetchCompanies = useCallback(async () => {
			debouncedFetchCompanies(keyword, sortBy);
		}, [debouncedFetchCompanies, keyword, sortBy]);

		useEffect(() => {
			if (!items.length && !isInSearchShortlist) {
				fetchCompanies();
			}
		}, [items, isInSearchShortlist, fetchCompanies]);

		const handlePageChange = (page) => {
			if (page >= 1 && page <= totalPages) {
				dispatch(getCompaniesByPage(page));
				const companyList = document.querySelector(".company-list");
				companyList.scrollIntoView({ top: 0, behavior: "smooth" });
			}
		};

		const download = async () => {
			if (!companies?.length || companies.length < 2) {
				return;
			}

			// If user has new shortlist report flag, show modal
			if (newShortlistReport) {
				setShowDownloadModal(true);
				return;
			}

			setIsLoading(true);

			try {

				if (!newShortlistReport) {
					axios({
						url:
							process.env.REACT_APP_API_URL +
							`/groups/print-pdf/${props.groupId}?companyId=${currentCompanyId}`,
						method: "GET",
						responseType: "blob", // important
					})
						.then((response) => {
							const url = window.URL.createObjectURL(new Blob([response.data]));
							const link = document.createElement("a");
							link.href = url;
							link.setAttribute("download", "shortlist.pdf");
							document.body.appendChild(link);
							link.click();
							setIsLoading(false);
						})
						.finally(() => {
							setIsLoading(false);
						});

					return;
				}

				const companyIds = companies.map((c) => c.id);
				const sectorIds = search.searchCriteria?.sector_ids || [];
				const result = await exportPdf(
					currentCompanyId,
					companyIds,
					undefined,
					sectorIds.length > 0 ? sectorIds : undefined,
					search.groupId || undefined
				);
				if (result?.download_url) {
					downloadFileNoPopup(result.download_url, result.filename || "shortlist-report.pdf", false);
				}
			} finally {
				setIsLoading(false);
			}
		};

		const createSupplierList = () => {
			setShowCreateDrawer(true);
		};

		const addSupplierToList = () => {
			setShowAddDrawer(true);
		};

		const onCloseCreateDrawer = () => {
			setShowCreateDrawer(false);
		};

		const onCloseAddDrawer = () => {
			setShowAddDrawer(false);
		};

		const onSearchSubmit = async (values) => {
			const cleanKeyword = values?.keyword?.trim() || "";
			setKeyword(cleanKeyword);

			if (!isInSearchShortlist) {
				await getCompanies(values?.keyword, sortBy);
			}
		};

		const changeSortBy = (selectedOption) => {
			const newValue = selectedOption
				? selectedOption.value
				: sortList[0].value;
			setSortBy(newValue);

			if (!isInSearchShortlist) {
				getCompanies(keyword, newValue);
			}
		};
		const handleRemove = useCallback(
			(companyId) => {
				removeFromList(companyId, "shortlist", props.groupId);
			},
			[removeFromList, props.groupId]
		);

		// Memoize the CompanyGrid component for better performance
		const CompanyGrid = useMemo(() => {
			// Always show skeleton when isDataLoading is true
			if (isDataLoading) {
				return (
					<Grid
						templateColumns="repeat(3, 1fr)"
						gap={6}
						columnGap={6}
						className="w-full"
					>
						{[...Array(6)].map((_, index) => (
							<GridItem
								key={index}
								className="relative bg-white"
							>
								<SkeletonBase
									width="100%"
									startColor="#c8ccd4"
									height="180px"
									className="border border-white"
								/>
								<SkeletonCircle
									size="140px"
									startColor="#c8ccd4"
									className="z-1 absolute left-1/2 -translate-x-1/2 -translate-y-1/2 border border-white"
								/>
								<SkeletonBase
									width="50%"
									startColor="#c8ccd4"
									height="16px"
									className="mx-auto mt-[100px]"
								/>
								<SkeletonBase
									width="50%"
									startColor="#c8ccd4"
									height="10px"
									className="mx-auto my-2"
								/>
							</GridItem>
						))}
					</Grid>
				);
			}

			if (!isDataLoading && companies.length > 0) {
				return (
					<div className="grid grid-cols-1 justify-between gap-4 lg:grid-cols-2 xl:grid-cols-3">
						{companies.map((item) => (
							<CompanyCard
								key={item.id}
								company={item}
								className={`col md-6 ${isInSearchShortlist ? "lg-3" : "lg-4"}`}
								companyGrouping={item?.grouping}
								handleRemoveButtonClick={
									isInSearchShortlist ? handleRemove : null
								}
								linkNewTab={linkNewTab}
								isSearchShortlist={isInSearchShortlist}
								isAdminMode={isAdminMode}
							/>
						))}
					</div>
				);
			}

			if (!isDataLoading && companies.length === 0) {
				return (
					<div className="col-span-full text-center">No Companies Found</div>
				);
			}

			// although with the current logic, one of the three conditions should always be true.
			return null;
		}, [
			isDataLoading,
			companies,
			isInSearchShortlist,
			isAdminMode,
			linkNewTab,
			handleRemove,
		]);

		return (
			<div className="w-full">
				<TabContent>
					{!noHeading && (
						<Heading size="xl">
							{isAdminMode ? "Companies Admin" : "Companies Listings"}
						</Heading>
					)}
					<Flex className="!w-full justify-end">
						<div>
							<Toolbar>
								<SearchBox
									placeholder="Search resources"
									handleFormSubmit={onSearchSubmit}
									keywordFilter
									whiteInput
									isCompanyList
								/>
								<div style={{ marginLeft: "20px" }}>
									<Input
										options={sortList}
										onChange={changeSortBy}
										value={sortBy}
										type="select"
										className="!max-h-[50px] !w-[250px]"
										size="base"
										placeholder="Sort by"
									/>
								</div>
							</Toolbar>
						</div>
					</Flex>
					<ShortlistStyled
						className="row company-list w-full"
						padded={padded}
						isInSearchShortlist={isInSearchShortlist}
					>
						{CompanyGrid}
						{!window.location.pathname.includes("shortlist") &&
							search?.searchResult?.length > 0 &&
							search?.paginationMeta?.total > 0 &&
							search?.paginationMeta?.per_page > 0 && (
								<Pagination
									currentPage={search?.paginationMeta?.current_page || 1}
									totalItems={search?.paginationMeta?.total || 0}
									itemsPerPage={
										search?.paginationMeta?.per_page || DEFAULT_PAGE_SIZE
									}
									onPageChange={handlePageChange}
								/>
							)}

						{companies?.length > 0 &&
							!window.location.pathname.includes("accounts") &&
							!window.location.pathname.includes("companies") && (
								<div className="mt-8">
									<p className="font-bold">
										Download a detailed comparison of your shortlisted
										suppliers.
									</p>
									<div className="mt-8 flex pt-2">
										<Button
											btntype="no-background-border"
											height="46px"
											className="mr-4 !p-0"
											onClick={download}
											disabled={isLoading}
										>
											{!isLoading ? (
												<>
													<span className="iconInline">
														<Icon icon="download" />
													</span>
													<div
														className="ml-2"
														data-testid="download-shortlist"
													>
														DOWNLOAD SHORTLIST
													</div>
												</>
											) : (
												<>Downloading</>
											)}
										</Button>
										{isRfxEnabled && (
											<>
												<Button
													btntype="blueBorder"
													height="46px"
													className="mr-3"
													onClick={() => addSupplierToList()}
													data-testid="add-to-existing-supplier-list"
												>
													Add to Existing Supplier list
												</Button>
												<Button
													btntype="primary"
													height="46px"
													onClick={() => createSupplierList()}
													data-testid="add-to-new-supplier-list"
												>
													Add to New Supplier list
												</Button>
											</>
										)}
									</div>
								</div>
							)}
					</ShortlistStyled>
				</TabContent>
				<CreateSupplierDrawer
					isOpen={showCreateDrawer}
					onClose={onCloseCreateDrawer}
					listIDCompanies={listIDCompanies}
				></CreateSupplierDrawer>
				<AddSupplierDrawer
					isOpen={showAddDrawer}
					onClose={onCloseAddDrawer}
					listCompanies={listCompanies}
					activeSupplierList={idSupplierList}
				></AddSupplierDrawer>
				{newShortlistReport && (
					<ShortlistDownloadModal
						isOpen={showDownloadModal}
						onClose={() => setShowDownloadModal(false)}
						currentCompanyId={currentCompanyId}
						context={{
							companyIds: companies.map((c) => c.id),
							searchSectorIds: search.searchCriteria?.sector_ids || [],
							ingress: "search",
							groupId: search.groupId || undefined,
						}}
						onCustomise={() => setShowCustomiseModal(true)}
					/>
				)}
				{canCustomise && newShortlistReport && (
					<CustomiseReportModal
						isOpen={showCustomiseModal}
						onClose={() => setShowCustomiseModal(false)}
						currentCompanyId={currentCompanyId}
						context={{
							companyIds: companies.map((c) => c.id),
							searchSectorIds: search.searchCriteria?.sector_ids || [],
							ingress: "search",
							groupId: search.groupId || undefined,
						}}
					/>
				)}
			</div>
		);
	}
);

ShortlistContainer.defaultProps = {
	items: [],
	initialSortBy: "name",
};

const mapStateToProps = (state) => {
	return {
		searchResult: state.search.searchResult,
		isSearching: state.search.isLoading,
		groupId: state.search.groupId,
		user: state.auth.user,
	};
};

const mapDispatchToProps = {
	getCompanies,
	removeFromList,
};

export const Shortlist = connect(
	mapStateToProps,
	mapDispatchToProps
)(ShortlistContainer);
